import { motion } from 'framer-motion';
import { Instagram, Linkedin, Youtube, Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { footerLinks, logos } from '../data/mock';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useTheme } from '../hooks/useTheme';

const iconMap = {
  Instagram: Instagram,
  Linkedin: Linkedin,
  Youtube: Youtube,
};

export const Footer = () => {
  const [ref, isVisible] = useScrollReveal();
  const { isDark } = useTheme();

  return (
    <footer ref={ref} className="relative pt-20 pb-8 w-full">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <img
              src={isDark ? logos.white : logos.blue}
              alt="Movii"
              className="h-24 w-auto object-contain mb-6 -ml-2"
              style={{ filter: isDark ? 'brightness(1.1)' : 'none' }}
            />
            <p className="text-slate-500 dark:text-white/40 text-sm leading-relaxed mb-6">
              Transformando marcas através do marketing digital. Estratégias que geram resultados reais.
            </p>
            <div className="flex gap-3">
              {footerLinks.social.map((social) => {
                const Icon = iconMap[social.icon];
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.08] flex items-center justify-center text-slate-500 dark:text-white/40 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-500/30 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors duration-300"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </motion.div>

          {/* Serviços */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-slate-900 dark:text-white font-semibold text-sm uppercase tracking-wider mb-6">Serviços</h4>
            <ul className="space-y-3">
              {footerLinks.servicos.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="group flex items-center gap-2 text-slate-500 dark:text-white/40 text-sm hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300"
                  >
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Empresa */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-slate-900 dark:text-white font-semibold text-sm uppercase tracking-wider mb-6">Empresa</h4>
            <ul className="space-y-3">
              {footerLinks.empresa.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="group flex items-center gap-2 text-slate-500 dark:text-white/40 text-sm hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300"
                  >
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contato */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-slate-900 dark:text-white font-semibold text-sm uppercase tracking-wider mb-6">Contato</h4>
            <ul className="space-y-4">
              <li>
                <a href={`mailto:${footerLinks.contato.email}`} className="flex items-center gap-3 text-slate-500 dark:text-white/40 text-sm hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300">
                  <Mail className="w-4 h-4 text-blue-500/50" />
                  {footerLinks.contato.email}
                </a>
              </li>
              <li>
                <a href={`tel:${footerLinks.contato.phone}`} className="flex items-center gap-3 text-slate-500 dark:text-white/40 text-sm hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300">
                  <Phone className="w-4 h-4 text-blue-500/50" />
                  {footerLinks.contato.phone}
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-500 dark:text-white/40 text-sm">
                <MapPin className="w-4 h-4 text-blue-500/50" />
                {footerLinks.contato.address}
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-slate-200 dark:border-white/[0.05] flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 dark:text-white/20 text-xs">
            © {new Date().getFullYear()} Movii. Todos os direitos reservados.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-slate-400 dark:text-white/20 text-xs hover:text-slate-600 dark:hover:text-white/40 transition-colors duration-300">
              Política de Privacidade
            </a>
            <a href="#" className="text-slate-400 dark:text-white/20 text-xs hover:text-slate-600 dark:hover:text-white/40 transition-colors duration-300">
              Termos de Uso
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
